import React, { useState } from 'react';
import { careerOpenings } from '../mockData';
import { Briefcase, MapPin, Clock, Building } from 'lucide-react';
import { toast } from 'sonner';

const Career = () => {
  const [selectedJob, setSelectedJob] = useState(null);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    position: '',
    experience: '',
    resume: null,
    coverLetter: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    setFormData(prev => ({ ...prev, resume: e.target.files[0] }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Frontend only - show success message
    toast.success('Application submitted successfully! We will contact you soon.');
    setFormData({
      fullName: '',
      email: '',
      phone: '',
      position: '',
      experience: '',
      resume: null,
      coverLetter: ''
    });
    setSelectedJob(null);
  };

  return (
    <div className="page-container">
      {/* Page Header */}
      <section className="page-header">
        <h1 className="page-title">Join Our Team</h1>
        <p className="page-subtitle">
          Build your career with a company that values innovation, collaboration, and growth
        </p>
      </section>

      {/* Benefits Section */}
      <section className="benefits-section">
        <h2 className="section-title">Why Work With Us?</h2>
        <div className="benefits-grid">
          <div className="benefit-card">
            <h3 className="benefit-title">Competitive Compensation</h3>
            <p className="benefit-text">Industry-leading salaries and performance bonuses</p>
          </div>
          <div className="benefit-card">
            <h3 className="benefit-title">Growth Opportunities</h3>
            <p className="benefit-text">Continuous learning and career advancement programs</p>
          </div>
          <div className="benefit-card">
            <h3 className="benefit-title">Work-Life Balance</h3>
            <p className="benefit-text">Flexible working hours and remote work options</p>
          </div>
          <div className="benefit-card">
            <h3 className="benefit-title">Health & Wellness</h3>
            <p className="benefit-text">Comprehensive health insurance and wellness programs</p>
          </div>
        </div>
      </section>

      {/* Job Openings */}
      <section className="openings-section">
        <h2 className="section-title">Current Openings</h2>
        <div className="openings-grid">
          {careerOpenings.map((job) => (
            <div key={job.id} className="job-card">
              <h3 className="job-title">{job.title}</h3>
              <div className="job-details">
                <div className="job-detail">
                  <MapPin size={16} />
                  <span>{job.location}</span>
                </div>
                <div className="job-detail">
                  <Briefcase size={16} />
                  <span>{job.type}</span>
                </div>
                <div className="job-detail">
                  <Clock size={16} />
                  <span>{job.experience}</span>
                </div>
                <div className="job-detail">
                  <Building size={16} />
                  <span>{job.department}</span>
                </div>
              </div>
              <button 
                className="btn-apply"
                onClick={() => {
                  setSelectedJob(job);
                  setFormData(prev => ({ ...prev, position: job.title }));
                  window.scrollTo({ top: document.querySelector('.application-form').offsetTop - 100, behavior: 'smooth' });
                }}
              >
                Apply Now
              </button>
            </div>
          ))}
        </div>
      </section>

      {/* Application Form */}
      <section className="application-form">
        <h2 className="section-title">Submit Your Application</h2>
        <form onSubmit={handleSubmit} className="career-form">
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="fullName" className="form-label">Full Name *</label>
              <input
                type="text"
                id="fullName"
                name="fullName"
                value={formData.fullName}
                onChange={handleInputChange}
                className="form-input"
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="email" className="form-label">Email *</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className="form-input"
                required
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="phone" className="form-label">Phone Number *</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                className="form-input"
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="position" className="form-label">Position Applied For *</label>
              <select
                id="position"
                name="position"
                value={formData.position}
                onChange={handleInputChange}
                className="form-input"
                required
              >
                <option value="">Select a position</option>
                {careerOpenings.map((job) => (
                  <option key={job.id} value={job.title}>{job.title}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="experience" className="form-label">Years of Experience *</label>
            <input
              type="text"
              id="experience"
              name="experience"
              value={formData.experience}
              onChange={handleInputChange}
              className="form-input"
              placeholder="e.g., 5 years"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="resume" className="form-label">Upload Resume (PDF) *</label>
            <input
              type="file"
              id="resume"
              name="resume"
              onChange={handleFileChange}
              className="form-input-file"
              accept=".pdf,.doc,.docx"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="coverLetter" className="form-label">Cover Letter</label>
            <textarea
              id="coverLetter"
              name="coverLetter"
              value={formData.coverLetter}
              onChange={handleInputChange}
              className="form-textarea"
              rows="5"
              placeholder="Tell us why you're a great fit for this position..."
            ></textarea>
          </div>

          <button type="submit" className="btn-submit">
            Submit Application
          </button>
        </form>
      </section>
    </div>
  );
};

export default Career;
